import React, { useEffect, useState } from 'react';

import { Box, Button, ButtonSpinner, ButtonText, Heading, Text, VStack } from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import BottomAction from '../../../../shared/components/bottomAction/bottomAction';
import { errorHandler } from '../../../../shared/components/errorHandler/errorHander';
import MediaUploader from '../../../../shared/components/media/mediaUploader';
import errorToast from '../../../../shared/components/toast/errorToast';
import successToast from '../../../../shared/components/toast/successToast';
import WebScrollView from '../../../../shared/components/web/webScrollView';
import DatePicker from '../../../../shared/generic/DatePicker';
import { useMaxWidth } from '../../../../shared/provider/maxWidthProvider';
import { firebaseApp } from '../../../../shared/services/firebase/firebaseFactory';
import { Metrics } from '../../../../shared/themes';
import { carDocumentTypes } from '../../../../shared/util/car-utils';
import { cleanFirestoreListResult } from '../../../../shared/util/firestore-utils';
import { userDocumentStatus } from '../../../../shared/util/userDocument-utils';
import CarDocumentSkeleton from '../carDocumentSkeleton';

const emptyDocument = {
  status: userDocumentStatus.waintingApproval,
  type: carDocumentTypes.technicalInspection,
};

function TechnicalInspection({ route }) {
  const { carId } = route?.params ?? {};

  const [document, setDocument] = useState(emptyDocument);
  const [updating, setUpdating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bottomActionHeight, setBottomActionHeight] = useState(0);

  const { t } = useTranslation();
  const navigation = useNavigation();
  const maxWidth = useMaxWidth();

  const currentUser = useSelector((state) => state.users.currentUser);

  function valid() {
    return document?.images?.length > 0 && document?.technicalControlDate;
  }

  function SaveDocuments() {
    if (valid()) {
      setUpdating(true);
      if (!document?.uid) {
        firebaseApp
          .firestore()
          .collection('cars')
          .doc(carId)
          .collection('documents')
          .add(document)
          .then(() => {
            setUpdating(false);
            successToast(t('carDocuments.successfullyAdd'));
            navigation.canGoBack() && navigation.goBack();
          })
          .catch((e) => {
            errorHandler(e, true);
            setUpdating(false);
          });
      } else {
        firebaseApp
          .firestore()
          .collection('cars')
          .doc(carId)
          .collection('documents')
          .doc(document?.uid)
          .update(document)
          .then(() => {
            setUpdating(false);
            successToast(t('carDocuments.successfullyAdd'));
            navigation.canGoBack() && navigation.goBack();
          })
          .catch((e) => {
            errorHandler(e, true);
            setUpdating(false);
          });
      }
    } else {
      errorToast(t('error.completeForm'));
    }
  }

  function updateImages(images) {
    const cleanImages = images.filter((image) => image?.url !== undefined);
    if (cleanImages && cleanImages?.length > 0) {
      setDocument((prevDoc) => {
        return { ...prevDoc, images: cleanImages };
      });
    }
  }

  useEffect(() => {
    if (currentUser?.uid && carId) {
      setLoading(true);
      firebaseApp
        .firestore()
        .collection('cars')
        .doc(carId)
        .collection('documents')
        .where('type', '==', carDocumentTypes.technicalInspection)
        .get()
        .then(cleanFirestoreListResult)
        .then((res) => {
          if (res?.length > 0) {
            setDocument(res[0]);
          } else {
            setDocument(emptyDocument);
          }
          setLoading(false);
        })
        .catch((e) => {
          errorHandler(e, true);
          setLoading(false);
        });
    }
  }, [currentUser, carId]);

  if (loading) {
    return <CarDocumentSkeleton />;
  }

  return (
    <>
      <WebScrollView testID={'scrollView'} hideFooter={true} maxWidth={maxWidth > 700 ? 700 : maxWidth}>
        <VStack my={'$4'} mx={'$screenMargin'} space={'md'} mb={bottomActionHeight}>
          <Text variant={'grey'}>{t('carDocuments.insurance.info')}</Text>
          <VStack space={'sm'}>
            <Heading>{t('addCar.insuranceValidDateTitle')}</Heading>
            <DatePicker
              key={document?.technicalControlDate}
              title={t('addCar.insuranceValidDateTitle')}
              date={document?.technicalControlDate ? new Date(document?.technicalControlDate * 1000) : new Date()}
              onConfirm={(date) => {
                setDocument((prev) => ({ ...prev, technicalControlDate: date.getTime() / 1000 }));
              }}
            />
          </VStack>
          <VStack space={'sm'}>
            <Heading>{t('carDocuments.insurance.addCertificate')}</Heading>
            <MediaUploader
              key={document?.uid}
              testID={'idDocument'}
              initialList={document?.images ?? []}
              onUpdate={(images) => updateImages(images)}
              storagePath={'user-documents'}
              multipleSelectionAllow={true}
              document={true}
            />
          </VStack>
        </VStack>
      </WebScrollView>
      <BottomAction
        onLayout={(event) => {
          setBottomActionHeight(event?.nativeEvent?.layout?.height ?? 0);
        }}>
        <Box px={'$screenMargin'} my={'$4'} w={maxWidth > 700 ? 700 : maxWidth} alignSelf={'center'}>
          <Button testID="button.addDocument" isDisabled={updating || !valid()} onPress={SaveDocuments}>
            {updating && <ButtonSpinner mr="$1" />}
            <ButtonText>{t('global.validate')}</ButtonText>
          </Button>
        </Box>
      </BottomAction>
    </>
  );
}

export default TechnicalInspection;
